import { effectiveCategory, effectiveParentId, indexProjects } from '../model/project-tree';
import { isLive } from '../model/types';
import type { ProjectRecord, TimeBlockRecord } from '../model/types';
import { resolveSegments } from '../stats/segments';
import { formatMin } from '../time/personal-day';
import { zonedWallToIso } from '../time/tz';

export const BLOCKS_CSV_COLUMNS = [
  'date',
  'start',
  'end',
  'minutes',
  'countedMin',
  'project',
  'category',
  'status',
  'title',
  'note',
  'startIso',
  'endIso',
  /** Name of the parent project when `project` is a child project, else empty (project hierarchy). */
  'parent',
] as const;

const FORMULA_PREFIX = /^[=+\-@\t\r]/;

/** Quote per RFC 4180; neutralise spreadsheet formulas in user-entered text. */
function csvCell(value: string, userText = false): string {
  const safe = userText && FORMULA_PREFIX.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

/**
 * CSV of the live blocks (any status), ordered by date and start. `start` and `end` are wall
 * times (HH:MM); `startIso` and `endIso` are UTC instants computed from the block's time zone,
 * empty when that zone is not valid. `minutes` is the block's own duration (overlapping blocks
 * both list it); `countedMin` is what the union-based statistics attribute to the block (data
 * dictionary: drafts and overlap minutes lost to another block count 0), so the `countedMin`
 * sums per project match the statistics pages. Lines end with CRLF; text cells that start with
 * a formula character get a leading apostrophe so spreadsheets do not execute them.
 */
export function toBlocksCsv(blocks: readonly TimeBlockRecord[], projects: readonly ProjectRecord[]): string {
  const projectById = indexProjects(projects);
  const countedMin = new Map<string, number>();
  for (const segment of resolveSegments(blocks)) {
    countedMin.set(
      segment.blockId,
      (countedMin.get(segment.blockId) ?? 0) + (segment.endAbs - segment.startAbs),
    );
  }
  const rows = blocks
    .filter(isLive)
    .sort(
      (a, b) =>
        (a.data.personalDate < b.data.personalDate
          ? -1
          : a.data.personalDate > b.data.personalDate
            ? 1
            : 0) ||
        a.data.startMin - b.data.startMin ||
        a.data.endMin - b.data.endMin ||
        (a.id < b.id ? -1 : 1),
    )
    .map((block) => {
      const { personalDate, startMin, endMin, tz, projectId, status, note, title } = block.data;
      const project = projectById.get(projectId);
      const parentId = project === undefined ? null : effectiveParentId(project, projectById);
      return [
        csvCell(personalDate),
        csvCell(formatMin(startMin)),
        csvCell(formatMin(endMin)),
        csvCell(String(endMin - startMin)),
        csvCell(String(countedMin.get(block.id) ?? 0)),
        csvCell(project?.data.name ?? '', true),
        csvCell(project === undefined ? '' : effectiveCategory(project, projectById)),
        csvCell(status),
        // Block title (2026-10b §5.1): empty for untitled blocks.
        csvCell(title ?? '', true),
        csvCell(note ?? '', true),
        csvCell(zonedWallToIso(personalDate, startMin, tz) ?? ''),
        csvCell(zonedWallToIso(personalDate, endMin, tz) ?? ''),
        csvCell(parentId === null ? '' : (projectById.get(parentId)?.data.name ?? ''), true),
      ].join(',');
    });
  return [BLOCKS_CSV_COLUMNS.join(','), ...rows].join('\r\n') + '\r\n';
}
